import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiBarChart2, FiUsers, FiClock, FiActivity, FiX, FiDownload, FiFileText, FiChevronDown, FiSearch, FiExternalLink, FiChevronUp, FiCopy, FiMove, FiNavigation, FiInfo } from 'react-icons/fi';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import TicketDetails from '../components/TicketDetails';
import { useUser } from '../contexts/UserContext';
import { STATIC_PRIORITY_OPTIONS, STATIC_STATUS_OPTIONS } from '../pages/TicketListPage';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899'];
const IMAGE_BASE = (process.env.REACT_APP_IMAGEURL || '').replace(/\/$/, '');
const DEFAULT_PROFILE_IMG = `${IMAGE_BASE}/uploads/no_image.png`;

const getProfileImg = (item) => {
    if (!item?.image) return DEFAULT_PROFILE_IMG;
    const img = item.image;
    return img.startsWith('http') ? img : `${IMAGE_BASE}/${img.replace(/^\//, '')}`;
};

const getVhCounts = (d) => ({
    external: Math.max(0, parseInt(d.external_count || 0, 10) - parseInt(d.external_pending_count || 0, 10)),
    // Match Leadtrends INTERNAL column: open internal tickets excluding status 12 (pending)
    internal: Math.max(0, parseInt(d.internal_count || 0, 10) - parseInt(d.internal_pending_count || 0, 10)),
    pending: parseInt(d.pending_cust_count || 0, 10),
});

const buildPendingBreakdown = (d) => {
    const merged = {};
    [...(d.customer_breakdown || []), ...(d.internal_customer_breakdown || [])].forEach((cb) => {
        const pending = parseInt(cb.pending_count || 0, 10);
        if (pending <= 0) return;
        const key = cb.cust_key || cb.customer_code || 'unknown';
        if (!merged[key]) {
            merged[key] = {
                ...cb,
                cust_key: cb.cust_key || key,
                customer_code: cb.customer_code || key,
                pending_count: 0,
                count: 0,
                net_count: 0,
            };
        }
        merged[key].pending_count += pending;
        merged[key].count = merged[key].pending_count;
        merged[key].net_count = merged[key].pending_count;
    });
    return Object.values(merged);
};

const escapeForOnclick = (value) => {
    const str = value == null ? '' : String(value);
    return "'" + str.replace(/'/g, "\\'") + "'";
};

// When External + Internal are both hidden via legend, auto-show Pending bars
const syncPendingSeriesVisibility = (chart) => {
    if (!chart?.series?.length) return;
    const ext = chart.series.find((s) => s.name === 'External Tickets');
    const int = chart.series.find((s) => s.name === 'Internal Tickets');
    const pending = chart.series.find((s) => s.name?.includes('Pending Customer Verification'));
    if (!ext || !int || !pending) return;
    if (!ext.visible && !int.visible) {
        pending.setVisible(true, false);
    }
};

// Whitelist attributes for Highcharts HTML tooltips
if (Highcharts.AST) {
    Highcharts.AST.allowedAttributes.push('onclick');
    Highcharts.AST.allowedAttributes.push('onmouseover');
    Highcharts.AST.allowedAttributes.push('onmouseout');
    Highcharts.AST.allowedAttributes.push('onerror');
}

const Verticalhead_wise_tickets = () => {
    const { user } = useUser();
    const navigate = useNavigate();
    const chartRef = useRef(null);

    // Filter states
    const [filterMonth, setFilterMonth] = useState('all');
    const [filterYear, setFilterYear] = useState('all');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const years = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i);

    const handlePrevMonth = () => {
        if (filterMonth === 'all') {
            if (filterYear !== 'all') setFilterYear(prev => (parseInt(prev) - 1).toString());
            return;
        }
        const currentIndex = months.indexOf(filterMonth);
        if (currentIndex === 0) {
            setFilterMonth(months[11]);
            if (filterYear !== 'all') setFilterYear(prev => (parseInt(prev) - 1).toString());
        } else {
            setFilterMonth(months[currentIndex - 1]);
        }
    };

    const handleNextMonth = () => {
        if (filterMonth === 'all') {
            if (filterYear !== 'all') setFilterYear(prev => (parseInt(prev) + 1).toString());
            return;
        }
        const currentIndex = months.indexOf(filterMonth);
        if (currentIndex === 11) {
            setFilterMonth(months[0]);
            if (filterYear !== 'all') setFilterYear(prev => (parseInt(prev) + 1).toString());
        } else {
            setFilterMonth(months[currentIndex + 1]);
        }
    };

    // Chart states
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [modalExportLoading, setModalExportLoading] = useState(false);
    const [view, setView] = useState('level1'); // 'level1', 'level2', 'level3'
    const [data, setData] = useState([]);
    const [history, setHistory] = useState([]);
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [context, setContext] = useState({ vhId: null, vhName: null, issueType: null, customerName: null, custKey: null });

    // Modal states
    const [modalConfig, setModalConfig] = useState({ show: false, loading: false, tickets: [], title: '' });
    const [modalSearch, setModalSearch] = useState('');
    const [modalPage, setModalPage] = useState(1);
    const [modalItemsPerPage, setModalItemsPerPage] = useState(10);
    const [modalSortConfig, setModalSortConfig] = useState({ key: null, direction: 'asc' });
    const [copiedTicketId, setCopiedTicketId] = useState(null);
    const [modalChartFilter, setModalChartFilter] = useState(null);

    const [showDrilldownChart, setShowDrilldownChart] = useState(false);
    const [drilldownChartHeight, setDrilldownChartHeight] = useState(340);
    const [isResizingDrilldown, setIsResizingDrilldown] = useState(false);

    const handleResize = useCallback((e) => {
        const modalBody = document.getElementById('drilldown-modal-body');
        if (!modalBody) return;
        const rect = modalBody.getBoundingClientRect();
        const availableHeight = rect.height;
        const newHeight = e.clientY - rect.top - 20;

        if (newHeight > 120 && newHeight < availableHeight - 200) {
            setDrilldownChartHeight(newHeight);
        }
    }, []);

    const startResizing = useCallback((e) => {
        e.preventDefault();
        setIsResizingDrilldown(true);
        window.addEventListener('mousemove', handleResize);
        window.addEventListener('mouseup', () => {
            setIsResizingDrilldown(false);
            window.removeEventListener('mousemove', handleResize);
        }, { once: true });
    }, [handleResize]);

    const resetDrilldownSize = useCallback(() => setDrilldownChartHeight(340), []);

    const activeDonutCharts = useMemo(() => {
        if (!modalConfig.tickets || modalConfig.tickets.length === 0) return [];

        const getSummary = (key) => {
            const summary = {};
            modalConfig.tickets.forEach(t => {
                let val = t[key];
                if (key === 'employee_name' && !val) val = 'Unassigned';
                if (key === 'customer_code' && !val) val = 'Unknown';
                const name = val || 'Unknown';
                if (!summary[name]) summary[name] = { name, y: 0 };
                summary[name].y++;
            });
            return Object.values(summary).sort((a, b) => b.y - a.y);
        };

        const list = [
            { id: 'status', key: 'status', label: 'Status', labelFull: 'Status Distribution', data: getSummary('status'), colorSet: COLORS, badge: 'Ticket State', badgeColor: 'blue' },
            { id: 'assignee', key: 'employee_name', label: 'Assignee', labelFull: 'Assignee Distribution', data: getSummary('employee_name'), colorSet: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'], badge: 'Team Load', badgeColor: 'emerald' },
            { id: 'customer', key: 'customer_code', label: 'Customer', labelFull: 'Customer Distribution', data: getSummary('customer_code'), colorSet: ['#f59e0b', '#fbbf24', '#fcd34d', '#fef3c7', '#fffbeb'], badge: 'Client Focus', badgeColor: 'amber' }
        ];
        return list.filter(c => c.data && c.data.length > 0);
    }, [modalConfig.tickets]);

    const getPremiumDonutOptions = (data, label, colorSet, filterKey) => {
        const total = data.reduce((sum, item) => sum + item.y, 0);
        return {
            chart: { type: 'pie', backgroundColor: 'transparent', height: 180, spacing: [0, 0, 0, 0], style: { fontFamily: 'inherit' } },
            title: {
                text: `<div style="text-align: center">
                        <span style="font-size: 8px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px">${label}</span><br/>
                        <span style="font-size: 14px; font-weight: 900; color: #0f172a">${total}</span><br/>
                        <span style="font-size: 7px; font-weight: 800; color: #cbd5e1; text-transform: uppercase; letter-spacing: 0.5px">Tickets</span>
                       </div>`,
                align: 'center', verticalAlign: 'middle', y: 0, useHTML: true
            },
            tooltip: {
                pointFormat: '<b>{point.name}</b>: {point.y} Tickets',
                backgroundColor: '#ffffff', borderWidth: 1, borderColor: '#e2e8f0', shadow: true,
                style: { fontSize: '10px', color: '#334155' }
            },
            plotOptions: {
                pie: {
                    innerSize: '65%', colors: colorSet || COLORS,
                    dataLabels: {
                        enabled: true,
                        allowOverlap: true,
                        crop: false,
                        overflow: 'justify',
                        format: '<span style="font-weight: 900; color: #475569">{point.name}</span>: <span style="font-weight: 900; color: #ef4444">{point.y}</span>',
                        style: { fontSize: '8px', textOutline: 'none', fontWeight: '900' },
                        padding: 0, connectorWidth: 1.2, connectorColor: '#cbd5e1', distance: 10, overflow: 'none'
                    },
                    showInLegend: false, borderWidth: 2, borderColor: '#ffffff',
                    point: {
                        events: {
                            click: function () {
                                setModalChartFilter({ key: filterKey, value: this.name, label: label });
                                setModalPage(1);
                            }
                        }
                    }
                }
            },
            series: [{ name: 'Tickets', data: data }],
            credits: { enabled: false }, exporting: { enabled: false }
        };
    };

    const filteredModalTickets = useMemo(() => {
        return modalConfig.tickets.filter(t => {
            if (modalChartFilter) {
                let tVal = t[modalChartFilter.key];
                if (modalChartFilter.key === 'employee_name' && !tVal) tVal = 'Unassigned';
                if (modalChartFilter.key === 'customer_code' && !tVal) tVal = 'Unknown';
                if (tVal !== modalChartFilter.value) return false;
            }

            if (!modalSearch) return true;
            const term = modalSearch.toLowerCase();
            return (
                (t.id && t.id.toString().toLowerCase().includes(term)) ||
                (t.title && t.title.toLowerCase().includes(term)) ||
                (t.employee_name && t.employee_name.toLowerCase().includes(term)) ||
                (t.customer_code && t.customer_code.toLowerCase().includes(term))
            );
        });
    }, [modalConfig.tickets, modalSearch, modalChartFilter]);

    const sortedModalTickets = useMemo(() => {
        let sortableItems = [...filteredModalTickets];
        if (modalSortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[modalSortConfig.key] || '';
                let bValue = b[modalSortConfig.key] || '';

                if (modalSortConfig.key === 'id') {
                    aValue = parseInt(aValue);
                    bValue = parseInt(bValue);
                } else if (modalSortConfig.key === 'created_date' || modalSortConfig.key === 'updated_date') {
                    aValue = new Date(aValue).getTime();
                    bValue = new Date(bValue).getTime();
                }

                if (aValue < bValue) return modalSortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return modalSortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [filteredModalTickets, modalSortConfig]);

    const handleModalSort = (key) => {
        let direction = 'asc';
        if (modalSortConfig.key === key && modalSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setModalSortConfig({ key, direction });
    };

    const handleCopyTitle = (id, title) => {
        navigator.clipboard.writeText(`#${id} - ${title}`);
        setCopiedTicketId(id);
        toast.success("Title Copied!");
        setTimeout(() => setCopiedTicketId(null), 2000);
    };

    const fetchData = useCallback(async (currentView, params = {}) => {
        setLoading(true);
        try {
            let endpoint = '';
            if (currentView === 'level1') endpoint = 'charts/vh-ticket-overview';
            else if (currentView === 'level2') endpoint = 'charts/vh-type-drilldown';
            else if (currentView === 'level3') endpoint = 'charts/vh-customer-drilldown';

            const finalParams = { ...params, filterMonth, filterYear };
            // External and internal bars exclude status 12; pending is its own series
            if (params.originType === 'external' || params.originType === 'internal') {
                finalParams.excludePending = 1;
            }

            const response = await api.get(endpoint, { params: finalParams });
            if (response.data.status) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching chart data:', error);
            toast.error('Failed to load chart data');
        } finally {
            setLoading(false);
        }
    }, [filterMonth, filterYear]);

    useEffect(() => {
        // Reset view when filter changes
        setView('level1');
        setHistory([]);
        setContext({ vhId: null, vhName: null, issueType: null, customerName: null });
        fetchData('level1');
    }, [fetchData, filterMonth, filterYear]);

    const handleDrilldown = (point, seriesName) => {
        if (view === 'level1') {
            const vhId = point.id;
            const vhName = point.name;
            const originType = seriesName === 'External Tickets' ? 'external'
                : (seriesName === 'Internal Tickets' ? 'internal'
                    : (seriesName?.includes('Pending Customer Verification') ? 'pending' : null));
            setHistory([...history, { view, data, context: { ...context } }]);
            setContext({ ...context, vhId, vhName, originType });
            setView('level2');
            fetchData('level2', { vh_id: vhId, originType: originType });
        } else if (view === 'level2') {
            const issueType = point.name;
            setHistory([...history, { view, data, context: { ...context } }]);
            setContext({ ...context, issueType });

            if (issueType === 'Enhancement') {
                setView('level2b');
                fetchData('level2', { vh_id: context.vhId, parent_issue_type: 'Enhancement', originType: context.originType });
            } else {
                setView('level3');
                fetchData('level3', { vh_id: context.vhId, issue_type: issueType, originType: context.originType });
            }
        } else if (view === 'level2b') {
            const enhancementType = point.name;
            setHistory([...history, { view, data, context: { ...context } }]);
            setContext({ ...context, enhancementType });
            setView('level3');
            fetchData('level3', {
                vh_id: context.vhId,
                issue_type: context.issueType,
                enhancement_type: enhancementType,
                originType: context.originType
            });
        } else if (view === 'level3') {
            const customerName = point.name;
            const custKey = point.cust_key;
            setContext({ ...context, customerName, custKey });
            fetchTicketDetails(context.vhId, context.issueType, context.enhancementType, custKey, customerName, context.originType);
        }
    };

    const handleBackTo = (targetView) => {
        if (targetView === 'level1' && view !== 'level1') {
            setView('level1');
            setHistory([]);
            setContext({ vhId: null, vhName: null, issueType: null, customerName: null });
            fetchData('level1');
        } else if (targetView === 'level2' && (view === 'level3' || view === 'level2b') && context.vhId) {
            setView('level2');
            setHistory(history.length > 0 ? [history[0]] : []);
            setContext({ vhId: context.vhId, vhName: context.vhName, originType: context.originType, issueType: null, customerName: null });
            fetchData('level2', { vh_id: context.vhId, originType: context.originType });
        }
    };

    const handleBack = () => {
        if (history.length > 0) {
            const lastState = history[history.length - 1];
            setView(lastState.view);
            setData(lastState.data);
            setContext(lastState.context);
            setHistory(history.slice(0, -1));
        }
    };

    const fetchTicketDetails = useCallback(async (vhId, issueType, enhancementType, custKey, clientName, originType) => {
        setModalPage(1);
        setModalSearch('');
        setModalChartFilter(null);
        const modalTitle = originType === 'pending'
            ? `Pending Customer Verification - ${clientName}`
            : `Total Opened Tickets - ${clientName}`;
        setModalConfig({ show: true, loading: true, tickets: [], title: modalTitle });
        try {
            const finalParams = {
                vh_id: vhId,
                issue_type: issueType,
                enhancement_type: enhancementType,
                client_name: custKey || clientName,
                filterMonth,
                filterYear,
                originType
            };
            if (originType === 'external' || originType === 'internal') {
                finalParams.excludePending = 1;
            }
            const res = await api.get('charts/vh-ticket-details', {
                params: finalParams
            });
            if (res.data.status) {
                setModalConfig({
                    show: true,
                    loading: false,
                    tickets: res.data.data,
                    title: modalTitle
                });
            }
        } catch (error) {
            console.error('Failed to fetch ticket details:', error);
            toast.error('Unable to fetch detailed ticket metrics');
            setModalConfig(prev => ({ ...prev, loading: false }));
        }
    }, [filterMonth, filterYear]);

    // Register global handler for tooltip clicks
    useEffect(() => {
        window.openCustomerDetails = (vhId, custKey, customerName, originType) => {
            fetchTicketDetails(vhId, 'All', null, custKey, customerName, originType);
        };
        return () => { delete window.openCustomerDetails; };
    }, [fetchTicketDetails]);

    const handleTicketClick = (ticketId) => {
        navigate(`/?tab=dashboard&ticket=${ticketId}`);
        setModalConfig(prev => ({ ...prev, show: false }));
    };

    const getSeriesVisibility = useCallback(() => {
        const chart = chartRef.current?.chart;
        if (!chart || view !== 'level1') {
            return { showExternal: true, showInternal: true, showPending: true };
        }
        const ext = chart.series.find((s) => s.name === 'External Tickets');
        const int = chart.series.find((s) => s.name === 'Internal Tickets');
        const pending = chart.series.find((s) => s.name?.includes('Pending Customer Verification'));
        return {
            showExternal: ext?.visible !== false,
            showInternal: int?.visible !== false,
            showPending: pending?.visible !== false,
        };
    }, [view]);

    const buildExportTable = useCallback(() => {
        if (view === 'level1') {
            const vis = getSeriesVisibility();
            const headers = ['Name'];
            if (vis.showExternal) headers.push('External');
            if (vis.showInternal) headers.push('Internal');
            if (vis.showPending) headers.push('Pending Customer Verification');
            headers.push('Total');

            let totExternal = 0;
            let totInternal = 0;
            let totPending = 0;
            let totAll = 0;

            const body = data.map((d) => {
                const c = getVhCounts(d);
                const row = [d.name];
                if (vis.showExternal) {
                    row.push(c.external);
                    totExternal += c.external;
                }
                if (vis.showInternal) {
                    row.push(c.internal);
                    totInternal += c.internal;
                }
                if (vis.showPending) {
                    row.push(c.pending);
                    totPending += c.pending;
                }
                const rowTotal =
                    (vis.showExternal ? c.external : 0) +
                    (vis.showInternal ? c.internal : 0) +
                    (vis.showPending ? c.pending : 0);
                totAll += rowTotal;
                row.push(rowTotal);
                return row;
            });

            const footer = ['Total'];
            if (vis.showExternal) footer.push(totExternal);
            if (vis.showInternal) footer.push(totInternal);
            if (vis.showPending) footer.push(totPending);
            footer.push(totAll);

            return { headers, body, footer };
        }

        const headers = ['Name', 'Total Count', 'Internal', 'External'];
        const body = data.map((item) => [
            item.name,
            parseInt(item.count, 10) || 0,
            parseInt(item.internal_count, 10) || 0,
            parseInt(item.external_count, 10) || 0,
        ]);
        const footer = [
            'Total',
            data.reduce((s, d) => s + (parseInt(d.count, 10) || 0), 0),
            data.reduce((s, d) => s + (parseInt(d.internal_count, 10) || 0), 0),
            data.reduce((s, d) => s + (parseInt(d.external_count, 10) || 0), 0),
        ];
        return { headers, body, footer };
    }, [view, data, getSeriesVisibility]);

    const renderLoaderOverlay = (message = 'Syncing Details...') => (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-80 z-20">
            <div className="flex flex-col items-center py-24 gap-4">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                <p className="text-xs font-black text-red-600 uppercase tracking-[0.2em] animate-pulse">{message}</p>
            </div>
        </div>
    );

    // Export Logic Using jsPDF and ExcelJS
    const exportToExcel = async () => {
        setExportLoading(true);
        try {
            const { headers, body, footer } = buildExportTable();
            const colCount = headers.length;
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Analytics');
            const reportTitle = view === 'level1' ? 'Vertical Head Open Tickets & Pending Customer Verification ' :
                view === 'level2' ? `Issue Types - ${context.vhName}` :
                    `Customers - ${context.vhName} (${context.issueType})`;

            const companyRow = worksheet.addRow(['eNova Software and Hardware Solutions P Ltd']);
            worksheet.mergeCells(1, 1, 1, colCount);
            companyRow.font = { bold: true, size: 14, color: { argb: 'FFFF0000' } };
            companyRow.alignment = { horizontal: 'center', vertical: 'middle' };

            const dateStr = format(new Date(), 'dd MMM yyyy HH:mm');
            const subtitleRow = worksheet.addRow([`${reportTitle} (Generated: ${dateStr})`]);
            worksheet.mergeCells(2, 1, 2, colCount);
            subtitleRow.font = { italic: true, size: 10, color: { argb: 'FF444444' } };
            subtitleRow.alignment = { horizontal: 'center', vertical: 'middle' };

            const headerRow = worksheet.addRow(headers);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF555555' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            body.forEach((rowValues) => {
                const row = worksheet.addRow(rowValues);
                row.eachCell((cell, colNumber) => {
                    cell.alignment = { horizontal: colNumber === 1 ? 'left' : 'center', vertical: 'middle' };
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                });
            });

            const totalRow = worksheet.addRow(footer);
            totalRow.font = { bold: true };
            totalRow.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
            });

            worksheet.getColumn(1).width = 35;
            for (let i = 2; i <= colCount; i++) {
                worksheet.getColumn(i).width = 18;
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Ticket_Analytics_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            a.click();
            toast.success('Excel Downloaded Successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to export Excel');
        } finally {
            setExportLoading(false);
        }
    };

    const exportToPDF = async () => {
        setExportLoading(true);
        try {
            const { headers, body, footer } = buildExportTable();
            const doc = new jsPDF('p', 'pt', 'a4');
            const dateStr = format(new Date(), 'dd/MM/yyyy');
            const title = view === 'level1' ? 'Vertical Head Open Tickets & Pending Customer Verification' :
                view === 'level2' ? `Issue Types - ${context.vhName}` :
                    `Customers - ${context.vhName} (${context.issueType})`;

            autoTable(doc, {
                startY: 105,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                head: [headers],
                body,
                foot: [footer],
                theme: 'grid',
                headStyles: { fillColor: [51, 51, 51], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
                footStyles: { fillColor: [51, 51, 51], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
                styles: { fontSize: 10, cellPadding: 8 },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: () => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    doc.setFontSize(18);
                    doc.setTextColor(180, 0, 0);
                    doc.setFont('helvetica', 'bold');
                    doc.text("eNova Software and Hardware Solutions P Ltd", pageWidth / 2, 35, { align: 'center' });

                    doc.setFontSize(13);
                    doc.setTextColor(20, 20, 20);
                    doc.text(title, pageWidth / 2, 58, { align: 'center' });

                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });

                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.line(30, 88, pageWidth - 30, 88);
                }
            });

            doc.save(`Ticket_Analytics_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('PDF Downloaded Successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to export PDF');
        } finally {
            setExportLoading(false);
        }
    };

    const exportModalToExcel = async (ticketsToExport, title) => {
        setModalExportLoading(true);
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Ticket Details');

            // Row 1: Company Title
            const titleRow = worksheet.addRow(['eNova Software and Hardware Solutions P Ltd']);
            worksheet.mergeCells('A1:H1');
            titleRow.font = { bold: true, size: 14, color: { argb: 'FFFF0000' } };
            titleRow.alignment = { horizontal: 'center', vertical: 'middle' };

            // Row 2: Subtitle with Date
            const dateStr = format(new Date(), 'dd MMM yyyy HH:mm');
            const subtitleRow = worksheet.addRow([`${title} (Generated: ${dateStr})`]);
            worksheet.mergeCells('A2:H2');
            subtitleRow.font = { italic: true, size: 10, color: { argb: 'FF444444' } };
            subtitleRow.alignment = { horizontal: 'center', vertical: 'middle' };
            for (let i = 1; i <= 8; i++) {
                subtitleRow.getCell(i).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAEAEA' } };
            }

            // Set Column Widths Manually (Avoids overwriting Row 1)
            worksheet.getColumn(1).width = 6;
            worksheet.getColumn(2).width = 12;
            worksheet.getColumn(3).width = 60;
            worksheet.getColumn(4).width = 18;
            worksheet.getColumn(5).width = 25;
            worksheet.getColumn(6).width = 15;
            worksheet.getColumn(7).width = 22;
            worksheet.getColumn(8).width = 22;

            // Row 3: Headers
            const headers = ['S.No', 'Ticket ID', 'Title', 'Status', 'Issue Type', 'Assigned To', 'Origin', 'Aging', 'Created At', 'Updated At'];
            const headerRow = worksheet.getRow(3);
            headerRow.values = headers;

            headerRow.eachCell((cell, colNumber) => {
                if (colNumber <= headers.length) {
                    cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF555555' } };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = {
                        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
                    };
                }
            });

            // Data Rows
            ticketsToExport.forEach((t, idx) => {
                const row = worksheet.addRow([
                    idx + 1,
                    t.id,
                    t.title,
                    t.status,
                    t.issue_type || '-',
                    t.employee_name || 'Unassigned',
                    t.role == 2 ? 'External' : 'Internal',
                    t.aging_display || '-',
                    t.created_date ? format(new Date(t.created_date), 'dd/MM/yyyy HH:mm:ss') : '-',
                    t.updated_date ? format(new Date(t.updated_date), 'dd/MM/yyyy HH:mm:ss') : '-'
                ]);

                row.eachCell((cell, colNumber) => {
                    cell.alignment = { horizontal: colNumber === 3 ? 'left' : 'center', vertical: 'middle' };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FF000000' } },
                        left: { style: 'thin', color: { argb: 'FF000000' } },
                        bottom: { style: 'thin', color: { argb: 'FF000000' } },
                        right: { style: 'thin', color: { argb: 'FF000000' } }
                    };
                });
            });

            worksheet.getColumn(1).width = 8;
            worksheet.getColumn(2).width = 15;
            worksheet.getColumn(3).width = 60;
            worksheet.getColumn(4).width = 15;
            worksheet.getColumn(5).width = 25;
            worksheet.getColumn(6).width = 15;
            worksheet.getColumn(7).width = 22;
            worksheet.getColumn(8).width = 22;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Ticket_Details_${format(new Date(), 'yyyyMMdd')}.xlsx`;
            a.click();
            toast.success('Excel Downloaded Successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to export Excel');
        } finally {
            setModalExportLoading(false);
        }
    };

    const exportModalToPDF = async (ticketsToExport, title) => {
        setModalExportLoading(true);
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const dateStr = format(new Date(), 'dd/MM/yyyy');

            autoTable(doc, {
                startY: 105,
                margin: { top: 105, left: 30, right: 30, bottom: 50 },
                head: [['ID', 'Title', 'Status', 'Issue Type', 'Assignee', 'Origin', 'Aging', 'Created']],
                body: ticketsToExport.map(t => [
                    t.id,
                    t.title.length > 50 ? t.title.substring(0, 47) + '...' : t.title,
                    t.status,
                    t.issue_type || '-',
                    t.employee_name || 'Unassigned',
                    t.role == 2 ? 'External' : 'Internal',
                    t.aging_display || '-',
                    t.created_date ? format(new Date(t.created_date), 'dd/MM/yy') : '-'
                ]),
                theme: 'grid',
                headStyles: { fillColor: [51, 51, 51], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
                styles: { fontSize: 8, cellPadding: 5 },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                didDrawPage: (hookData) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    doc.setFontSize(18);
                    doc.setTextColor(180, 0, 0);
                    doc.setFont('helvetica', 'bold');
                    doc.text("eNova Software and Hardware Solutions P Ltd", pageWidth / 2, 35, { align: 'center' });

                    doc.setFontSize(13);
                    doc.setTextColor(20, 20, 20);
                    doc.text(title, pageWidth / 2, 58, { align: 'center' });

                    doc.setFontSize(9);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });

                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.line(30, 88, pageWidth - 30, 88);
                }
            });

            doc.save(`Ticket_Details_${format(new Date(), 'yyyyMMdd')}.pdf`);
            toast.success('PDF Downloaded Successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to export PDF');
        } finally {
            setModalExportLoading(false);
        }
    };

    const chartOptions = useMemo(() => {
        return {
            chart: {
                type: 'column',
                backgroundColor: 'transparent',
                style: { fontFamily: 'Inter, sans-serif' },
                height: 420,
                spacingBottom: 40,
                spacingTop: 0,
                spacingLeft: 10,
                spacingRight: 10,
                events: {
                    load: function () { syncPendingSeriesVisibility(this); },
                    redraw: function () { syncPendingSeriesVisibility(this); }
                }
            },
            title: { text: null },
            xAxis: {
                type: 'category',
                categories: data.map(d => d.name),
                labels: {
                    useHTML: view === 'level1',
                    overflow: 'allow',
                    crop: false,
                    autoRotation: false,
                    padding: 0,
                    formatter: function () {
                        if (view === 'level1') {
                            const item = data.find(d => d.name === this.value);
                            const img = `<img src="${getProfileImg(item)}" onerror="this.onerror=null;this.src='${DEFAULT_PROFILE_IMG}'" style="width: 28px; height: 28px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); margin-bottom: 4px;"/>`;
                            return `<div style="display: flex; flex-direction: column; align-items: center; text-align: center; white-space: nowrap; font-weight: 800; color: #334155;">
                                ${img}
                                <span style="font-size: 10px; letter-spacing: -0.025em; line-height: 1.2;">${this.value}</span>
                            </div>`;
                        }
                        return `<span style="font-weight: 700; color: #475569; font-size: 11px;">${this.value}</span>`;
                    },
                    style: { fontSize: '12px', fontWeight: '800' }
                },
                gridLineWidth: 0,
                lineWidth: 0
            },
            yAxis: {
                title: { text: 'Ticket Count', style: { color: '#94a3b8', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase' } },
                gridLineColor: '#f1f5f9',
                labels: { style: { color: '#94a3b8', fontSize: '10px' } }
            },
            legend: { enabled: false },
            plotOptions: {
                series: {
                    borderRadius: 8,
                    cursor: 'pointer',
                    point: {
                        events: {
                            click: function () {
                                const vhName = this.name || this.category;
                                const fromData = data.find((d) => d.name === vhName);
                                const clickedPoint = {
                                    ...(fromData || {}),
                                    id: this.id ?? this.options?.id ?? fromData?.id,
                                    name: vhName,
                                };
                                if (view === 'level1' ? clickedPoint.id : clickedPoint.name) {
                                    handleDrilldown(clickedPoint, this.series.name);
                                }
                            }
                        }
                    },
                    dataLabels: {
                        enabled: true,
                        format: '{point.y}',
                        style: { fontSize: '12px', fontWeight: '900', color: '#1e293b', textOutline: 'none' }
                    },
                    animation: { duration: 800, easing: 'easeOutBounce' },
                    states: {
                        hover: {
                            brightness: 0.05,
                            shadow: {
                                color: 'rgba(0, 0, 0, 0.1)',
                                offsetX: 0,
                                offsetY: 4,
                                width: 10
                            }
                        }
                    }
                },
                column: {
                    colorByPoint: view !== 'level1',
                    colors: COLORS,
                    pointPadding: 0.1,
                    groupPadding: 0.1,
                    borderWidth: 0,
                    maxPointWidth: 200,
                    minPointLength: 10,
                    stickyTracking: false
                }
            },
            legend: {
                enabled: view === 'level1',
                align: 'right',
                verticalAlign: 'top',
                itemStyle: { fontSize: '10px', fontWeight: 'bold', color: '#475569' }
            },
            tooltip: {
                useHTML: true,
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                borderRadius: 15,
                borderWidth: 0,
                shadow: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    offsetX: 0,
                    offsetY: 4,
                    opacity: 1,
                    width: 10
                },
                style: { pointerEvents: 'auto' },
                stickOnContact: true,
                hideDelay: 1000,
                positioner: function (labelWidth, labelHeight, point) {
                    // Position tooltip to the right of the bar, closer to pointer
                    let x = point.plotX + this.chart.plotLeft + 15;
                    let y = point.plotY + this.chart.plotTop - (labelHeight / 2);

                    // Prevent overflow on the right
                    if (x + labelWidth > this.chart.chartWidth) {
                        x = point.plotX + this.chart.plotLeft - labelWidth - 15;
                    }

                    return { x: x, y: Math.max(10, y) };
                },
                outside: false,
                formatter: function () {
                    const hasBreakdown = this.point.customer_breakdown && this.point.customer_breakdown.length > 0;
                    const pointId = this.point.id ?? this.point.options?.id;
                    const seriesLabel = (this.series.name || '').includes('Pending Customer Verification')
                        ? 'Pending Customer Verification'
                        : (this.series.name === 'Tickets' ? 'Total' : this.series.name);
                    let html = `<div style="padding: 10px; min-width: ${hasBreakdown ? '220px' : '150px'}; font-family: 'Inter', sans-serif; pointer-events: auto;">`;

                    // Header with optional profile image
                    const item = data.find(d => d.name === this.key);
                    const imgHtml = view === 'level1' ? `<img src="${getProfileImg(item)}" onerror="this.onerror=null;this.src='${DEFAULT_PROFILE_IMG}'" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover; margin-right: 8px; border: 1px solid #f1f5f9;"/>` : '';

                    html += `<div style="${hasBreakdown ? 'border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; margin-bottom: 8px;' : ''} display: flex; justify-content: space-between; align-items: center;">
                                <div style="display: flex; align-items: center;">
                                    ${imgHtml}
                                    <div style="display: flex; flex-direction: column;">
                                        <span style="font-size: 8px; color: #94a3b8; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em;">${view === 'level1' ? 'Vertical Head' : 'Category'}</span>
                                        <span style="font-size: 11px; color: #1e293b; font-weight: 800;">${this.key}</span>
                                    </div>
                                </div>
                                <div style="display: flex; flex-direction: column; align-items: flex-end; margin-left: 15px;">
                                    <span style="font-size: 8px; color: #94a3b8; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; max-width: 90px; text-align: right; line-height: 1.2; white-space: normal; word-wrap: break-word;">${seriesLabel}</span>
                                    <span style="font-size: 16px; color: ${this.series.color || '#ef4444'}; font-weight: 900; line-height: 1;">${this.y}</span>
                                </div>
                             </div>`;

                    // Customer Breakdown
                    if (hasBreakdown) {
                        html += `<div style="margin-top: 4px; pointer-events: auto;">
                                    <div style="font-size: 8px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; display: flex; align-items: center; gap: 4px;">
                                        Customer Breakup (Click to view)
                                    </div>
                                    <div style="max-height: 250px; overflow-y: auto; padding-right: 4px; margin-right: -4px; scrollbar-width: thin; pointer-events: auto;">
                                        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;">`;

                        const originType = this.series.name === 'External Tickets' ? 'external'
                            : (this.series.name === 'Internal Tickets' ? 'internal' : 'pending');
                        const vhPointId = pointId ?? item?.id;
                        this.point.customer_breakdown.forEach((cb) => {
                            const custKey = cb.cust_key || cb.customer_code || '';
                            const customerCode = cb.customer_code || custKey;
                            const countVal = cb.net_count !== undefined ? cb.net_count : cb.count;
                            html += `<div 
                                        onclick="window.openCustomerDetails(${escapeForOnclick(vhPointId)}, ${escapeForOnclick(custKey)}, ${escapeForOnclick(customerCode)}, ${escapeForOnclick(originType)})"
                                        style="background: #ffffff; border: 1.5px solid #e2e8f0; padding: 6px 10px; border-radius: 10px; display: flex; flex-direction: column; align-items: center; gap: 2px; cursor: pointer; transition: all 0.2s; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); pointer-events: auto;"
                                        onmouseover="this.style.borderColor='#8B5CF6';this.style.background='#faf5ff'"
                                        onmouseout="this.style.borderColor='#e2e8f0';this.style.background='#ffffff'"
                                     >
                                        <span style="font-size: 9px; font-weight: 800; color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 80px; pointer-events: none;">${customerCode}</span>
                                        <span style="font-size: 12px; font-weight: 900; color: #1e293b; pointer-events: none;">${countVal}</span>
                                     </div>`;
                        });

                        html += `</div></div></div>`;
                    }

                    html += `</div>`;
                    return html;
                },
                followPointer: false
            },
            series: view === 'level1' ? [
                {
                    name: 'External Tickets',
                    color: '#3B82F6', // Blue
                    events: {
                        legendItemClick: function () {
                            setTimeout(() => syncPendingSeriesVisibility(this.chart), 0);
                        }
                    },
                    data: data.map(d => {
                        const breakdown = (d.customer_breakdown || [])
                            .map(cb => ({
                                ...cb,
                                net_count: parseInt(cb.count) - parseInt(cb.pending_count || 0)
                            }))
                            .filter(cb => cb.net_count > 0);

                        return {
                            name: d.name,
                            y: Math.max(0, parseInt(d.external_count || 0) - parseInt(d.external_pending_count || 0)),
                            id: d.id,
                            cust_key: d.cust_key,
                            customer_breakdown: breakdown,
                            total_count: Math.max(0, parseInt(d.count || 0) - parseInt(d.pending_cust_count || 0))
                        };
                    })
                },
                {
                    name: 'Internal Tickets',
                    color: '#F97316', // Orange
                    events: {
                        legendItemClick: function () {
                            setTimeout(() => syncPendingSeriesVisibility(this.chart), 0);
                        }
                    },
                    data: data.map(d => {
                        const breakdown = (d.internal_customer_breakdown || [])
                            .map(cb => ({
                                ...cb,
                                net_count: Math.max(0, (parseInt(cb.count, 10) || 0) - (parseInt(cb.pending_count || 0, 10)))
                            }))
                            .filter(cb => cb.net_count > 0);

                        const internalNet = Math.max(0, parseInt(d.internal_count || 0, 10) - parseInt(d.internal_pending_count || 0, 10));

                        return {
                            name: d.name,
                            y: internalNet,
                            id: d.id,
                            cust_key: null,
                            customer_breakdown: breakdown,
                            total_count: internalNet
                        };
                    })
                },
                {
                    name: 'Pending Customer Verification (' + data.reduce((sum, d) => sum + parseInt(d.pending_cust_count || 0), 0) + ')',
                    color: '#8B5CF6', // Purple
                    visible: true,
                    data: data.map(d => ({
                        name: d.name,
                        y: parseInt(d.pending_cust_count || 0),
                        id: d.id,
                        customer_breakdown: buildPendingBreakdown(d)
                    })),
                    events: {
                        legendItemClick: function () {
                            setTimeout(() => syncPendingSeriesVisibility(this.chart), 0);
                        }
                    }
                }
            ] : [{
                name: 'Tickets',
                data: data.map(d => ({
                    name: d.name,
                    y: parseInt(d.count),
                    id: d.id,
                    cust_key: d.cust_key,
                    customer_breakdown: d.customer_breakdown
                }))
            }],
            exporting: { enabled: false },
            credits: { enabled: false },
            responsive: {
                rules: [{
                    condition: {
                        maxWidth: 768
                    },
                    chartOptions: {
                        tooltip: {
                            enabled: false
                        },
                        xAxis: {
                            labels: {
                                style: { fontSize: '9px' }
                            }
                        }
                    }
                }]
            }
        };
    }, [view, data, context, isMobile]);

    return (
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden group hover:shadow-2xl transition-all duration-500">
            {/* Header */}
            <div className="px-6 py-3 border-b border-slate-50 flex flex-col sm:flex-row justify-between items-center bg-gradient-to-r from-white to-slate-50 gap-4 sm:gap-0">
                <div className="flex items-center gap-3 self-start sm:self-center">
                    <div className={`p-2 rounded-xl ${view === 'level1' ? 'bg-blue-50 text-blue-600' : view === 'level2' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'} transition-colors duration-300`}>
                        {view === 'level1' ? <FiUsers size={20} /> : view === 'level2' ? <FiActivity size={20} /> : <FiClock size={20} />}
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-slate-800 tracking-tight">
                            {view === 'level1' ? 'Ticket Tracking: Vertical Heads & Heads' :
                                view === 'level2' ? 'Issue Type Analytics' :
                                    view === 'level2b' ? 'Enhancement Breakdown' : 'Customer Distribution'}
                        </h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mt-0.5">
                            {view === 'level1' ? 'Open Tickets And Pending Customer Verification & Confirmation' :
                                view === 'level2' ? `Categorized for ${context.vhName}${context.originType === 'external' ? ' — External Open Tickets' : context.originType === 'internal' ? ' — Internal Open Tickets' : context.originType === 'pending' ? ' — Pending Customer Verification' : ''}` :
                                    view === 'level2b' ? `Types for ${context.vhName}` :
                                        `Client Breakup: ${context.enhancementType || context.issueType}`}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-3 w-full sm:w-auto">
                    {history.length > 0 && (
                        <button
                            onClick={handleBack}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-all text-xs font-bold shadow-sm active:scale-95"
                        >
                            <FiChevronLeft size={16} />
                            Back
                        </button>
                    )}

                    <div className="flex items-center gap-2">
                        <div className="relative group">
                            <select
                                value={filterMonth}
                                onChange={(e) => setFilterMonth(e.target.value)}
                                className="appearance-none pl-3 pr-8 py-1.5 text-[12px] font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all cursor-pointer shadow-sm min-w-[100px] outline-none"
                            >
                                <option value="all">All Months</option>
                                {months.map((m, i) => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                        </div>

                        <div className="relative group">
                            <select
                                value={filterYear}
                                onChange={(e) => setFilterYear(e.target.value)}
                                className="appearance-none pl-3 pr-8 py-1.5 text-[12px] font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all cursor-pointer shadow-sm min-w-[80px] outline-none"
                            >
                                <option value="all">All Year</option>
                                {years.map(y => <option key={y} value={y.toString()}>{y}</option>)}
                            </select>
                            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={14} />
                        </div>
                    </div>

                    <div className="flex items-center gap-1.5 px-3 py-1 bg-white rounded-full border border-slate-200 shadow-sm h-9">
                        <button
                            onClick={exportToExcel}
                            disabled={exportLoading || loading}
                            className="p-1.5 text-emerald-500 hover:bg-emerald-50 hover:text-emerald-600 rounded-md transition-colors disabled:opacity-40 disabled:pointer-events-none"
                            title="Export to Excel"
                        >
                            <FiDownload strokeWidth={3} size={15} />
                        </button>
                        <button
                            onClick={exportToPDF}
                            disabled={exportLoading || loading}
                            className="p-1.5 text-red-400 hover:bg-red-50 hover:text-red-500 rounded-md transition-colors disabled:opacity-40 disabled:pointer-events-none"
                            title="Export to PDF"
                        >
                            <FiFileText strokeWidth={3} size={15} />
                        </button>
                    </div>

                    <div className="hidden sm:block h-8 w-[1px] bg-slate-200 mx-1" />
                </div>
            </div>

            {/* Chart Content */}
            <div className="p-4 relative min-h-[450px]">
                <AnimatePresence mode="wait">
                    {(loading || exportLoading) ? (
                        <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-80 z-10"
                        >
                            <div className="flex flex-col items-center py-24 gap-4">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                                <p className="text-xs font-black text-red-600 uppercase tracking-[0.2em] animate-pulse">
                                    {exportLoading ? 'Generating Report...' : 'Syncing Details...'}
                                </p>
                            </div>
                        </motion.div>
                    ) : data.length === 0 ? (
                        <motion.div
                            key="empty"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col items-center justify-center h-[350px] text-slate-300"
                        >
                            <FiBarChart2 size={48} className="mb-4 opacity-20" />
                            <p className="text-sm font-bold">No open tickets found for this selection</p>
                        </motion.div>
                    ) : (
                        <motion.div
                            key={view}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Breadcrumbs / Status Footer */}
            <div className="px-6 py-2 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                    <button
                        onClick={() => handleBackTo('level1')}
                        className={`text-[9px] font-black uppercase tracking-widest transition-colors hover:text-blue-600 ${view === 'level1' ? 'text-blue-600' : 'text-slate-400'}`}
                    >
                        Vertical Heads
                    </button>
                    {history.length > 0 && <FiChevronLeft className="text-slate-300 rotate-180" size={10} />}
                    {context.vhName && (
                        <button
                            onClick={() => handleBackTo('level2')}
                            className={`text-[9px] font-black uppercase tracking-widest transition-colors hover:text-emerald-600 ${view === 'level2' ? 'text-emerald-600' : 'text-slate-400'}`}
                        >
                            {context.vhName}
                        </button>
                    )}
                    {(view === 'level2b' || view === 'level3' || history.length > 1) && <FiChevronLeft className="text-slate-300 rotate-180" size={10} />}
                    {context.issueType && (
                        <span className={`text-[9px] font-black uppercase tracking-widest ${view === 'level2b' ? 'text-amber-600' : 'text-slate-400'}`}>
                            {context.issueType}
                        </span>
                    )}
                    {(view === 'level3' && context.enhancementType) && <FiChevronLeft className="text-slate-300 rotate-180" size={10} />}
                    {(view === 'level3' && context.enhancementType) && (
                        <span className={`text-[9px] font-black uppercase tracking-widest text-amber-600`}>
                            {context.enhancementType}
                        </span>
                    )}
                    {(view === 'level3' && !context.enhancementType) && (
                        <span className={`text-[9px] font-black uppercase tracking-widest text-amber-600`}>
                            {context.issueType}
                        </span>
                    )}
                </div>
                <div className="text-[10px] font-bold text-slate-400 italic">
                    Click on bars to drill down
                </div>
            </div>

            {/* Ticket Details Modal */}
            <AnimatePresence>
                {modalConfig.show && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex justify-center items-center p-4 bg-slate-900/50 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="bg-white rounded-[2rem] shadow-2xl w-[96vw] max-w-[1600px] max-h-[96vh] flex flex-col overflow-hidden border border-slate-100"
                        >
                            <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex flex-col xl:flex-row gap-4 justify-between items-start xl:items-center bg-slate-50 relative">
                                <div className="flex-1 min-w-0 pr-10 xl:pr-0 flex flex-col gap-1 items-start">
                                    {modalChartFilter && (
                                        <button
                                            onClick={() => setModalChartFilter(null)}
                                            className="px-2 py-1 bg-red-100 text-red-600 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all flex items-center gap-1 shrink-0"
                                            title="Clear Chart Filter"
                                        >
                                            <FiX size={10} /> {modalChartFilter.label}: {modalChartFilter.value}
                                        </button>
                                    )}
                                    <h3 className="text-base sm:text-lg md:text-xl font-black text-slate-800 tracking-tight break-words leading-tight">{modalConfig.title}</h3>
                                    <motion.p
                                        animate={{ opacity: [1, 0.4, 1] }}
                                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                        className="text-[9px] sm:text-[10px] font-bold text-red-500 uppercase tracking-tighter"
                                    >
                                        Note: Click on Ticket ID or Title to view full details in the dashboard.
                                    </motion.p>
                                </div>

                                {/* Mobile-only Close Button (Top Right Absolute) */}
                                <button
                                    onClick={() => { setModalConfig(prev => ({ ...prev, show: false })); setModalSearch(''); setModalChartFilter(null); }}
                                    className="xl:hidden absolute top-4 right-4 p-2 bg-slate-200/50 hover:bg-red-50 text-slate-500 hover:text-red-600 rounded-xl transition-all"
                                >
                                    <FiX size={18} />
                                </button>

                                <div className="flex flex-wrap xl:flex-nowrap items-center gap-2 sm:gap-4 shrink-0 w-full xl:w-auto justify-between xl:justify-end">
                                    <div className="hidden sm:flex items-center gap-2 border-r border-slate-100 pr-4">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Rows</span>
                                        <div className="relative group/rows flex items-center">
                                            <select
                                                value={modalItemsPerPage}
                                                onChange={(e) => {
                                                    const val = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
                                                    setModalItemsPerPage(val);
                                                    setModalPage(1);
                                                }}
                                                className="pl-3 pr-8 py-1.5 text-[11px] font-black text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all cursor-pointer shadow-sm min-w-[70px] outline-none appearance-none"
                                            >
                                                {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                                                <option value="all">All</option>
                                            </select>
                                            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                                        </div>
                                    </div>

                                    <div className="relative group flex-1 min-w-[150px]">
                                        <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-500 transition-colors" />
                                        <input
                                            type="text"
                                            placeholder="Search tickets..."
                                            value={modalSearch}
                                            onChange={(e) => { setModalSearch(e.target.value); setModalPage(1); }}
                                            className="pl-10 pr-4 h-10 w-full bg-white border border-slate-200 rounded-xl text-[12px] font-bold text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all outline-none shadow-sm"
                                        />
                                    </div>

                                    <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                        <div className="flex items-center gap-2 mr-2 px-3 py-1.5 bg-white rounded-xl border border-slate-200 shadow-sm group/toggle hidden sm:flex">
                                            <span className={`text-[9px] font-black uppercase tracking-widest ${showDrilldownChart ? 'text-red-500' : 'text-slate-400'}`}>Chart</span>
                                            <button
                                                onClick={() => setShowDrilldownChart(!showDrilldownChart)}
                                                className={`w-10 h-5 rounded-full relative transition-all duration-300 ${showDrilldownChart ? 'bg-red-500' : 'bg-slate-300'}`}
                                            >
                                                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all duration-300 ${showDrilldownChart ? 'left-6' : 'left-1'}`} />
                                            </button>
                                            {/* <span className="text-[8px] font-black uppercase tracking-widest text-slate-400">Layout</span> */}
                                        </div>

                                        <div className="flex items-center bg-white border border-slate-200 p-1 rounded-xl shadow-sm">
                                            <button
                                                onClick={() => exportModalToExcel(filteredModalTickets, modalConfig.title)}
                                                disabled={modalExportLoading || modalConfig.loading}
                                                title="Export to Excel"
                                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all disabled:opacity-40 disabled:pointer-events-none"
                                            >
                                                <FiDownload size={16} />
                                            </button>
                                            <button
                                                onClick={() => exportModalToPDF(filteredModalTickets, modalConfig.title)}
                                                disabled={modalExportLoading || modalConfig.loading}
                                                title="Export to PDF"
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-40 disabled:pointer-events-none"
                                            >
                                                <FiFileText size={16} />
                                            </button>
                                        </div>
                                        {/* Desktop Close Button */}
                                        <button
                                            onClick={() => { setModalConfig(prev => ({ ...prev, show: false })); setModalSearch(''); setModalChartFilter(null); }}
                                            className="hidden xl:flex p-2.5 bg-white border border-slate-200 hover:bg-red-50 text-slate-400 hover:text-red-600 hover:border-red-100 shadow-sm rounded-xl transition-all"
                                        >
                                            <FiX size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div id="drilldown-modal-body" className="flex-1 flex flex-col p-2 sm:p-8 custom-scrollbar bg-slate-50/30 overflow-y-auto relative">
                                {modalExportLoading && renderLoaderOverlay('Generating Report...')}
                                {modalConfig.loading ? (
                                    <div className="flex-1 flex flex-col items-center justify-center py-32 bg-white bg-opacity-80">
                                        <div className="flex flex-col items-center py-24 gap-4">
                                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                                            <p className="text-xs font-black text-red-600 uppercase tracking-[0.2em] animate-pulse">Syncing Details...</p>
                                        </div>
                                    </div>
                                ) : filteredModalTickets.length > 0 ? (
                                    <div className="flex-1 flex flex-col min-h-0 gap-2 relative">
                                        <AnimatePresence>
                                            {showDrilldownChart && activeDonutCharts.length > 0 && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                                                    animate={{
                                                        opacity: 1,
                                                        height: drilldownChartHeight,
                                                        marginBottom: 4,
                                                        transition: { duration: isResizingDrilldown ? 0 : 0.3 }
                                                    }}
                                                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                                                    className={`bg-white rounded-[2rem] p-6 border border-slate-100 shadow-xl flex flex-col gap-6 overflow-hidden relative shrink-0 ${isResizingDrilldown ? 'select-none' : ''}`}
                                                >
                                                    <div className="flex items-center gap-3 border-b border-slate-50 pb-2">
                                                        <div className="p-2 bg-red-50 rounded-xl">
                                                            <FiNavigation className="text-red-500" />
                                                        </div>
                                                        <div>
                                                            <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">Ticket Distribution Metrics</h3>
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Multi-Source Analytics View</p>
                                                        </div>
                                                    </div>

                                                    <div className={`h-full grid grid-cols-1 sm:grid-cols-3 gap-4 items-center ${isResizingDrilldown ? 'pointer-events-none' : ''}`}>
                                                        {activeDonutCharts.map((chart) => (
                                                            <div key={chart.id} className="h-full flex flex-col items-center group/chart relative bg-slate-50/20 rounded-3xl p-3 border border-slate-100/30 hover:bg-white hover:border-slate-200 transition-all duration-300">
                                                                <div className="flex items-center gap-2 mb-3 px-3 py-1.5 bg-white border border-slate-100 rounded-full shadow-sm z-10">
                                                                    <div className={`w-1.5 h-1.5 rounded-full ${chart.badgeColor === 'blue' ? 'bg-blue-400' : chart.badgeColor === 'emerald' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                                                                    <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest">{chart.labelFull}</span>
                                                                </div>
                                                                <div className="w-full flex-1 min-h-0 relative">
                                                                    <HighchartsReact
                                                                        highcharts={Highcharts}
                                                                        options={getPremiumDonutOptions(chart.data, chart.label, chart.colorSet, chart.key)}
                                                                        containerProps={{ style: { height: '100%', width: '100%' } }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div
                                                        onMouseDown={startResizing}
                                                        onDoubleClick={resetDrilldownSize}
                                                        className={`absolute bottom-0 left-0 right-0 h-6 cursor-ns-resize transition-all flex items-center justify-center z-30 group/handle ${isResizingDrilldown ? 'bg-slate-800/10' : 'hover:bg-slate-50'}`}
                                                        title="Drag up/down to resize | Double-click to reset"
                                                    >
                                                        <div className="flex flex-col items-center">
                                                            <div className={`flex items-center justify-center bg-white border border-slate-100 shadow-lg rounded-full px-2 py-1 transition-all ${isResizingDrilldown ? 'scale-125 border-red-400 bg-red-50 shadow-red-200/50' : 'group-hover/handle:scale-110 shadow-slate-200/50'}`}>
                                                                <FiMove size={14} className={`${isResizingDrilldown ? 'text-red-500' : 'text-slate-500 group-hover/handle:text-red-500'}`} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <div className="flex flex-col flex-1 min-h-0 bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                                            <div className="overflow-auto flex-1 custom-scrollbar">
                                                <table className="w-full text-left border-collapse">
                                                    <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10 shadow-sm">
                                                        <tr>
                                                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">S.No</th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('id')}
                                                            >
                                                                ID {modalSortConfig.key === 'id' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('title')}
                                                            >
                                                                Title {modalSortConfig.key === 'title' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('status')}
                                                            >
                                                                Status {modalSortConfig.key === 'status' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('issue_type')}
                                                            >
                                                                Issue Type {modalSortConfig.key === 'issue_type' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('employee_name')}
                                                            >
                                                                Assigned To {modalSortConfig.key === 'employee_name' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors text-center"
                                                                onClick={() => handleModalSort('aging_display')}
                                                            >
                                                                Aging {modalSortConfig.key === 'aging_display' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('created_date')}
                                                            >
                                                                Created {modalSortConfig.key === 'created_date' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                            <th
                                                                className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-red-600 transition-colors"
                                                                onClick={() => handleModalSort('updated_date')}
                                                            >
                                                                Last Updated {modalSortConfig.key === 'updated_date' && (modalSortConfig.direction === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {(() => {
                                                            const pSize = modalItemsPerPage === 'all' ? sortedModalTickets.length : modalItemsPerPage;
                                                            return sortedModalTickets.slice((modalPage - 1) * pSize, modalPage * pSize).map((t, i) => (
                                                                <tr key={i} className="hover:bg-blue-50/30 transition-colors group">
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-[11px] font-bold text-slate-400">{(modalPage - 1) * pSize + i + 1}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                if (e.ctrlKey || e.metaKey) {
                                                                                    window.open(`/tickets?tid=${t.id}`, '_blank');
                                                                                } else {
                                                                                    setSelectedTicketId(t.id);
                                                                                }
                                                                            }}
                                                                            className="text-[11px] font-black text-red-600 bg-red-50 px-2 py-1 rounded-lg border border-red-100 hover:bg-red-600 hover:text-white hover:shadow-md transition-all active:scale-95"
                                                                            title="Click to view details (Ctrl+Click to open in new tab)"
                                                                        >
                                                                            #{t.id}
                                                                        </button>
                                                                    </td>
                                                                    <td className="px-6 py-4 min-w-[300px]">
                                                                        <div className="flex flex-col gap-1 group/copy">
                                                                            <div className="flex items-start justify-between gap-2">
                                                                                <p
                                                                                    onClick={(e) => {
                                                                                        if (e.ctrlKey || e.metaKey) {
                                                                                            window.open(`/tickets?tid=${t.id}`, '_blank');
                                                                                        } else {
                                                                                            setSelectedTicketId(t.id);
                                                                                        }
                                                                                    }}
                                                                                    className="text-[13px] font-bold text-slate-700 leading-snug cursor-pointer hover:text-red-600 hover:underline transition-all"
                                                                                    title="Click to view details (Ctrl+Click to open in new tab)"
                                                                                >
                                                                                    {t.title}
                                                                                </p>
                                                                                <button
                                                                                    onClick={() => handleCopyTitle(t.id, t.title)}
                                                                                    className="shrink-0 p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all active:scale-90"
                                                                                    title="Copy Title"
                                                                                >
                                                                                    <FiCopy size={14} />
                                                                                </button>
                                                                            </div>
                                                                            {copiedTicketId === t.id && (
                                                                                <motion.div
                                                                                    initial={{ opacity: 0, y: -5 }}
                                                                                    animate={{ opacity: 1, y: 0 }}
                                                                                    className="flex items-center gap-1.5 text-emerald-600"
                                                                                >
                                                                                    <span className="text-[10px] font-black uppercase tracking-wider">✓ Copied</span>
                                                                                </motion.div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-tighter">
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
                                                                            {t.status}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-[11px] font-bold text-slate-700">{t.issue_type || '-'}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <span className="text-[11px] font-bold text-slate-700">{t.employee_name || 'Unassigned'}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4 text-center">
                                                                        <span className="inline-flex items-center justify-center px-3 py-1.5 bg-orange-50 text-orange-600 border border-orange-100 rounded-lg text-[10px] font-black min-w-[70px] leading-none whitespace-nowrap">{t.aging_display || '-'}</span>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex flex-col">
                                                                            <span className="text-[11px] font-bold text-slate-700">
                                                                                {t.created_date ? format(new Date(t.created_date), 'dd/MM/yyyy') : '-'}
                                                                            </span>
                                                                            <span className="text-[10px] font-bold text-slate-400 mt-0.5">
                                                                                {t.created_date ? format(new Date(t.created_date), 'HH:mm') : ''}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="flex flex-col">
                                                                            <span className="text-[11px] font-bold text-slate-700">
                                                                                {t.updated_date ? format(new Date(t.updated_date), 'dd/MM/yyyy') : '-'}
                                                                            </span>
                                                                            <span className="text-[10px] font-bold text-slate-400 mt-0.5">
                                                                                {t.updated_date ? format(new Date(t.updated_date), 'HH:mm') : ''}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ));
                                                        })()}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Pagination Footer */}
                                            {modalItemsPerPage !== 'all' && sortedModalTickets.length > modalItemsPerPage && (
                                                <div className="px-6 py-4 bg-white border-t border-slate-50 flex flex-col sm:flex-row justify-between items-center gap-4">
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em]">
                                                        Showing <span className="text-slate-600">{Math.min((modalPage - 1) * modalItemsPerPage + 1, sortedModalTickets.length)}</span> to <span className="text-slate-600">{Math.min(modalPage * modalItemsPerPage, sortedModalTickets.length)}</span> of <span className="text-slate-600">{sortedModalTickets.length}</span> Results
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            disabled={modalPage === 1}
                                                            onClick={() => setModalPage(p => Math.max(1, p - 1))}
                                                            className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all shadow-sm ${modalPage === 1 ? 'bg-slate-50 text-slate-200 cursor-not-allowed' : 'bg-white text-slate-400 hover:bg-slate-50 hover:text-red-500 border border-slate-100'}`}
                                                        >
                                                            <FiChevronLeft size={16} />
                                                        </button>

                                                        {(() => {
                                                            const totalPages = Math.ceil(sortedModalTickets.length / modalItemsPerPage);
                                                            const pages = [];
                                                            let startPage = Math.max(1, modalPage - 2);
                                                            let endPage = Math.min(totalPages, startPage + 3);
                                                            if (endPage - startPage < 3) startPage = Math.max(1, endPage - 3);

                                                            for (let i = startPage; i <= endPage; i++) {
                                                                pages.push(
                                                                    <button
                                                                        key={i}
                                                                        onClick={() => setModalPage(i)}
                                                                        className={`w-8 h-8 rounded-xl text-xs font-black transition-all shadow-sm ${modalPage === i ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'bg-white text-slate-500 border border-slate-50 hover:bg-slate-50'}`}
                                                                    >
                                                                        {i}
                                                                    </button>
                                                                );
                                                            }
                                                            return pages;
                                                        })()}

                                                        <button
                                                            disabled={modalPage >= Math.ceil(sortedModalTickets.length / modalItemsPerPage)}
                                                            onClick={() => setModalPage(p => p + 1)}
                                                            className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all shadow-sm ${modalPage >= Math.ceil(sortedModalTickets.length / modalItemsPerPage) ? 'bg-slate-50 text-slate-200 cursor-not-allowed' : 'bg-white text-slate-400 hover:bg-slate-50 hover:text-red-500 border border-slate-100'}`}
                                                        >
                                                            <FiChevronRight size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                                        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                            <FiSearch size={32} className="opacity-50" />
                                        </div>
                                        <h3 className="text-lg font-black text-slate-600">No Tickets Found</h3>
                                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">Adjust your search or filters</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {selectedTicketId && (
                <div className="relative z-[500]">
                    <TicketDetails
                        ticketId={selectedTicketId}
                        tickets={modalConfig?.tickets || []}
                        selectedTicketChildId="1"
                        priorityOptions={STATIC_PRIORITY_OPTIONS}
                        statusOptions={STATIC_STATUS_OPTIONS}
                        userInfo={user}
                        onClose={() => setSelectedTicketId(null)}
                        onSuccess={() => {
                            fetchData(view, context);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default Verticalhead_wise_tickets;
